/**
 * Teacher-only "Share to student" button that lives inside the live classroom
 * top bar. Uploads a file to the current lesson via the teacher materials
 * endpoint so it appears in the student's Resources section.
 */
import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../api";
import { useTeacherAuth } from "../TeacherAuthContext";

const ACCEPT_ATTR = ".pdf,.jpg,.jpeg,.png,.mp3,.mp4";
const ACCEPT_MIMES = [
  "application/pdf", "image/jpeg", "image/png",
  "audio/mpeg", "audio/mp3", "video/mp4",
];
const MAX_FILE_BYTES = 50 * 1024 * 1024;

export default function ShareToStudentButton({ lessonId }) {
  const { token } = useTeacherAuth();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function onPick(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ACCEPT_MIMES.includes(file.type)) {
      toast.error("Only PDF, JPG, PNG, MP3, MP4 are accepted.");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      toast.error("File is too large. Max 50 MB.");
      return;
    }
    setUploading(true);
    try {
      await api.teacherUploadMaterial(token, lessonId, file);
      toast.success(`"${file.name}" shared with the student`);
    } catch (err) {
      toast.error(err?.message || "Could not share.");
    } finally {
      setUploading(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid="classroom-share-btn"
        className="text-[11px] tracking-[0.28em] uppercase font-medium px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors disabled:opacity-40"
        title="Share a file with the student — appears in their Resources"
      >
        {uploading ? "Sharing…" : "Share to student ↑"}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTR}
        onChange={onPick}
        className="hidden"
        data-testid="classroom-share-input"
      />
    </>
  );
}
